// Visibility profiles describe which UI controls or layers should be visible
// for each high-level application mode. The profiles are intentionally declarative
// so that presentation components can apply the rules without hard-coding mode
// comparisons in multiple places.

export type HelperToggleKey =
  | "rulers"
  | "snap"
  | "guides"
  | "dots"
  | "lines"
  | "overlay";

export type HelperVisibilityConfig = {
  readonly visible: boolean;
  readonly allowed?: Partial<Record<HelperToggleKey, boolean>>;
};

export type BreakpointVisibilityConfig = {
  readonly visible: boolean;
  readonly variant?: "default" | "overlay";
};

export type InfoHUDVisibilityConfig = {
  readonly zoom: boolean;
  readonly status: boolean;
  readonly breakpoints: BreakpointVisibilityConfig;
  readonly languages: boolean;
  readonly themes: boolean;
  readonly helpers: HelperVisibilityConfig;
};

export type DominantSettingsVisibility = {
  readonly visible: boolean;
  readonly label?: string;
};

export type DominantVisibilityConfig = {
  readonly visible: boolean;
  readonly llmAudio: boolean;
  readonly llmButton: boolean;
  readonly map: { readonly visible: boolean; readonly variant?: string };
  readonly settings: {
    readonly sketch?: DominantSettingsVisibility;
    readonly page?: DominantSettingsVisibility;
    readonly project: DominantSettingsVisibility;
    readonly user: DominantSettingsVisibility;
  };
};

export type BottomVisibilityConfig = {
  readonly visible: boolean;
  readonly projects: { readonly visible: boolean };
  readonly showAll: { readonly visible: boolean; readonly label?: string };
  readonly pages: { readonly visible: boolean; readonly label?: string };
};

export type FlowControlsVisibilityConfig = {
  readonly preview: { readonly visible: boolean; readonly icon?: string };
  readonly annotate: { readonly visible: boolean; readonly icon?: string };
};

export type SidebarVisibilityConfig = {
  readonly visible: boolean;
  readonly flow: FlowControlsVisibilityConfig;
  readonly tools: { readonly visible: boolean };
  readonly modifiers: { readonly visible: boolean };
};

export type LayersVisibilityConfig = {
  readonly htmlRender: boolean;
  readonly canvas: boolean;
  readonly helpers: boolean;
  readonly dedicated: boolean;
};

export type VisibilityProfile = {
  readonly id: string;
  readonly infoHUD: InfoHUDVisibilityConfig;
  readonly dominant: DominantVisibilityConfig;
  readonly bottom: BottomVisibilityConfig;
  readonly sidebar: SidebarVisibilityConfig;
  readonly layers: LayersVisibilityConfig;
};

export type VisibilityProfileOptions = {
  readonly showAll?: {
    readonly edit?: boolean;
    readonly sketch?: boolean;
  };
};

type VisibilityProfileMap = {
  readonly [mode: string]: VisibilityProfile;
};

export const VISIBILITY_PROFILES: VisibilityProfileMap = {
  sketch: {
    id: "sketch",
    infoHUD: {
      zoom: true,
      status: true,
      breakpoints: {
        visible: true,
        variant: "overlay",
      },
      languages: false,
      themes: false,
      helpers: {
        visible: true,
        allowed: {
          dots: true,
          overlay: true,
          rulers: false,
          snap: false,
          guides: false,
          lines: false,
        },
      },
    },
    dominant: {
      visible: true,
      llmAudio: true,
      llmButton: false,
      map: { visible: true, variant: "interpretation-picker" },
      settings: {
        sketch: { visible: true, label: "Sketch Settings" },
        page: { visible: false },
        project: { visible: true, label: "Project Settings" },
        user: { visible: true, label: "User Preferences" },
      },
    },
    bottom: {
      visible: true,
      projects: { visible: true },
      showAll: { visible: true, label: "Sketches" },
      pages: { visible: true, label: "Sketches" },
    },
    sidebar: {
      visible: true,
      flow: {
        preview: { visible: true, icon: "preview_play" },
        annotate: { visible: true, icon: "interactions_mic" },
      },
      tools: { visible: true },
      modifiers: { visible: true },
    },
    layers: {
      htmlRender: false,
      canvas: true,
      helpers: true,
      dedicated: false,
    },
  },
  edit: {
    id: "edit",
    infoHUD: {
      zoom: true,
      status: true,
      breakpoints: {
        visible: true,
        variant: "default",
      },
      languages: true,
      themes: true,
      helpers: {
        visible: true,
        allowed: {
          rulers: true,
          snap: true,
          guides: true,
          dots: true,
          lines: true,
          overlay: false,
        },
      },
    },
    dominant: {
      visible: true,
      llmAudio: true,
      llmButton: true,
      map: { visible: true, variant: "minimap" },
      settings: {
        sketch: { visible: false },
        page: { visible: true, label: "Page Settings" },
        project: { visible: true, label: "Project Settings" },
        user: { visible: true, label: "User Preferences" },
      },
    },
    bottom: {
      visible: true,
      projects: { visible: true },
      showAll: { visible: true, label: "Pages" },
      pages: { visible: true, label: "Pages" },
    },
    sidebar: {
      visible: true,
      flow: {
        preview: { visible: true, icon: "preview_play" },
        annotate: { visible: true, icon: "interactions_annotate" },
      },
      tools: { visible: true },
      modifiers: { visible: true },
    },
    layers: {
      htmlRender: true,
      canvas: true,
      helpers: true,
      dedicated: false,
    },
  },
  preview: {
    id: "preview",
    infoHUD: {
      zoom: false,
      status: true,
      breakpoints: {
        visible: true,
        variant: "default",
      },
      languages: true,
      themes: true,
      helpers: {
        visible: false,
      },
    },
    dominant: {
      visible: false,
      llmAudio: false,
      llmButton: false,
      map: { visible: false },
      settings: {
        sketch: { visible: false },
        page: { visible: false },
        project: { visible: false, label: "Project Settings" },
        user: { visible: false, label: "User Preferences" },
      },
    },
    bottom: {
      visible: false,
      projects: { visible: false },
      showAll: { visible: false },
      pages: { visible: false },
    },
    sidebar: {
      visible: true,
      flow: {
        preview: { visible: true, icon: "preview_stop" },
        annotate: { visible: true, icon: "interactions_annotate" },
      },
      tools: { visible: false },
      modifiers: { visible: true },
    },
    layers: {
      htmlRender: true,
      canvas: false,
      helpers: false,
      dedicated: false,
    },
  },
  ds: {
    id: "ds",
    infoHUD: {
      zoom: false,
      status: true,
      breakpoints: { visible: false },
      languages: false,
      themes: true,
      helpers: { visible: false },
    },
    dominant: {
      visible: true,
      llmAudio: true,
      llmButton: true,
      map: { visible: true, variant: "design-system" },
      settings: {
        sketch: { visible: false },
        page: { visible: true, label: "Design System Settings" },
        project: { visible: true, label: "Project Settings" },
        user: { visible: true, label: "User Preferences" },
      },
    },
    bottom: {
      visible: true,
      projects: { visible: true },
      showAll: { visible: false },
      pages: { visible: true, label: "Sources" },
    },
    sidebar: {
      visible: true,
      flow: {
        preview: { visible: true, icon: "preview_play" },
        annotate: { visible: true, icon: "interactions_mic" },
      },
      tools: { visible: true },
      modifiers: { visible: true },
    },
    layers: {
      htmlRender: false,
      canvas: false,
      helpers: false,
      dedicated: true,
    },
  },
  cms: {
    id: "cms",
    infoHUD: {
      zoom: false,
      status: true,
      breakpoints: { visible: false },
      languages: false,
      themes: false,
      helpers: { visible: false },
    },
    dominant: {
      visible: true,
      llmAudio: true,
      llmButton: true,
      map: { visible: true, variant: "cms-dashboard" },
      settings: {
        sketch: { visible: false },
        page: { visible: true, label: "CMS Settings" },
        project: { visible: true, label: "Project Settings" },
        user: { visible: true, label: "User Preferences" },
      },
    },
    bottom: {
      visible: true,
      projects: { visible: true },
      showAll: { visible: false },
      pages: { visible: false },
    },
    sidebar: {
      visible: true,
      flow: {
        preview: { visible: false, icon: "preview_play" },
        annotate: { visible: true, icon: "interactions_mic" },
      },
      tools: { visible: true },
      modifiers: { visible: true },
    },
    layers: {
      htmlRender: false,
      canvas: false,
      helpers: false,
      dedicated: true,
    },
  },
};

const FALLBACK_PROFILE = VISIBILITY_PROFILES.edit!;

export const resolveVisibilityProfile = (
  mode: string | null | undefined,
  options: VisibilityProfileOptions = {}
): VisibilityProfile => {
  if (!mode) return FALLBACK_PROFILE;

  const profile = VISIBILITY_PROFILES[
    mode as keyof typeof VISIBILITY_PROFILES
  ] as VisibilityProfile | undefined;
  if (profile !== undefined) {
    if (mode === "edit" && options.showAll?.edit) {
      return {
        ...profile,
        id: `${profile.id}-show-all`,
        layers: {
          ...profile.layers,
          htmlRender: false,
          canvas: false,
          helpers: false,
          dedicated: true,
        },
        sidebar: {
          ...profile.sidebar,
          flow: {
            ...profile.sidebar.flow,
            preview: { visible: false },
            annotate: {
              ...profile.sidebar.flow.annotate,
              icon: "interactions_mic",
            },
          },
          tools: { ...profile.sidebar.tools, visible: false },
        },
      };
    }

    if (mode === "sketch" && options.showAll?.sketch) {
      return {
        ...profile,
        id: `${profile.id}-show-all`,
        layers: {
          ...profile.layers,
          htmlRender: false,
          canvas: false,
          helpers: false,
          dedicated: true,
        },
        sidebar: {
          ...profile.sidebar,
          tools: { ...profile.sidebar.tools, visible: false },
        },
      };
    }

    return profile;
  }
  return FALLBACK_PROFILE;
};
